import React, { Suspense } from 'react';
import PropTypes from 'prop-types';
import Payline from './Payline/index';
import Iban from './Iban/index';
import Clabe from './Clabe/index';
import InternationalBank from './InternationalBank/index';
import PaymentsOS from './PaymentsOS/index';
import Ingenico from './Ingenico/index';
import ExpandButton from './ExpandButton';
import { fetchPaymentConfig } from '../hooks/fetcher';
import i18n from './i18n';
import { I18nextProvider, Trans } from 'react-i18next';
import { getClientCapabilities, currencyFormat } from '../utils';
import Spinner from './Spinner';
import ErrorBlock from './ErrorBlock';
import './LyriaPay.css';

const EXPANDABLE_PSPS = ['payline', 'adyen', 'clabe', 'iban', 'international_bank', 'paymentsos', 'ingenico'];

const importAdyen = () => import('./Adyen');
const Adyen = React.lazy(importAdyen);

const LyriaPay = React.forwardRef(
    (
        {
            token,
            sandbox = false,
            lyriaUrl,
            expiryAsText = false,
            hidePayButton = false,
            onPaymentResult,
            lang,
            loader = <Spinner />
        },
        ref
    ) => {
        const [loading, setLoading] = React.useState(true);
        const [success, setSuccess] = React.useState(false);
        const [paymentMethods, setPaymentMethods] = React.useState([]);
        const [
            expandedPaymentMethodId,
            setExpandedPaymentMethodId
        ] = React.useState(null);
        const [showOnlyId, setShowOnlyId] = React.useState(null);
        const [errorCode, setErrorCode] = React.useState(null);
        const expandedPaymentMethodRef = React.useRef();

        // preload adyen
        // it's a big chunk of code so we don't want to include it in the main js file
        // but we don't want to wait until we need to show the component either
        React.useEffect(() => {
            importAdyen();
        }, []);

        React.useEffect(() => {
            // handle both fr_FR and fr-FR
            i18n.changeLanguage(lang.replace('_', '-'));
        }, [lang]);

        React.useImperativeHandle(ref, () => ({
            pay() {
                return (
                    expandedPaymentMethodRef.current &&
                    expandedPaymentMethodRef.current.pay()
                );
            }
        }));

        React.useEffect(() => {
            setLoading(true);
            fetchPaymentConfig({
                url: lyriaUrl,
                endpoint: '/paymentConfig',
                config: {
                    request_token: token,
                    client_capabilities: getClientCapabilities()
                }
            })
                .then(datas => {
                    setPaymentMethods(datas.data);
                    for (let paymentMethod of datas.data) {
                        if (
                            paymentMethod.expand && !paymentMethod.is_disabled &&
                            EXPANDABLE_PSPS.indexOf(paymentMethod.psp) > -1
                        ) {
                            setExpandedPaymentMethodId(paymentMethod.id);
                            break;
                        }
                    }
                    setLoading(false);
                })
                .catch(e => {
                    console.error(e);
                });
        }, [token, lyriaUrl]);

        const paymentResultCallback = res => {
            if (res.success) {
                // if the payment successed we display a loader because we don't want the user
                // to see the payment form again until the host redirects to its own success page
                setErrorCode(null);
                setSuccess(true);
            } else {
                setErrorCode(res.data?.code);
                // if the payment failed we want to show the other psps expand buttons again if any
                // to allow the user to select another payment method if needed
                setShowOnlyId(null);
            }
            onPaymentResult(res);
        };

        const paymentStartedCallback = paymentMethod => () => {
            setErrorCode(null);
            setShowOnlyId(paymentMethod.id);
        };

        const displaySpinner = loading || success;
        const paymentMethodsToDisplay = paymentMethods.filter(x => showOnlyId === null || showOnlyId === x.id);

        return (
            <I18nextProvider i18n={i18n}>
                {sandbox && <h2>Sandbox mode</h2>}
                <div className={`wz-lyriapay ${displaySpinner ? 'loading': ''}`}>
                    {errorCode && (
                        <ErrorBlock errorCode={errorCode} />
                    )}
                    {displaySpinner && (
                        <>{loader}</>
                    )}
                    {(!displaySpinner && paymentMethodsToDisplay.map(x => {
                        console.log('in dev version');
                        switch (x.psp) {
                            case 'payline':
                                if (x.id === expandedPaymentMethodId) {
                                    return (
                                        <Payline
                                            key={`expanded_${x.id}`}
                                            label={x.name}
                                            config={x.config}
                                            sandbox={sandbox}
                                            token={token}
                                            expiryAsText={expiryAsText}
                                            lyriaUrl={lyriaUrl}
                                            onPaymentStarted={
                                                paymentStartedCallback(x)
                                            }
                                            onPaymentResult={
                                                paymentResultCallback
                                            }
                                            hidePayButton={hidePayButton}
                                            ref={expandedPaymentMethodRef}
                                            loader={loader}
                                            savePaymentMethod={x.save_payment_method}
                                            savedPaymentMethods={x.saved_payment_methods}
                                            operation={x.operation}
                                        />
                                    );
                                } else {
                                    return (
                                        <ExpandButton
                                            key={`button_${x.id}`}
                                            onClick={() => {
                                                if (!x.is_disabled) {
                                                    setExpandedPaymentMethodId(
                                                        x.id
                                                    );
                                                }
                                            }
                                            }
                                            paymentMethods={x.payment_methods}
                                            isDisabled={x.is_disabled}
                                            minAmountForCreditRequired={currencyFormat(x.min_amount_for_credit_required, x.currency, lang)}
                                            refundFee={currencyFormat(x.fee, x.currency, lang)}
                                            isRefundContext={x.operation === 'credit'}
                                        />
                                    );
                                }
                            case 'adyen':
                                if (x.id === expandedPaymentMethodId) {
                                    return (
                                        <Suspense key={`expanded_${x.id}`} fallback={<>{loader}</>}>
                                            <Adyen
                                                label={x.name}
                                                config={x.config}
                                                sandbox={sandbox}
                                                token={token}
                                                expiryAsText={expiryAsText}
                                                lyriaUrl={lyriaUrl}
                                                onPaymentStarted={
                                                    paymentStartedCallback(x)
                                                }
                                                onPaymentResult={
                                                    paymentResultCallback
                                                }
                                                hidePayButton={hidePayButton}
                                                ref={expandedPaymentMethodRef}
                                                loader={loader}
                                                savePaymentMethod={x.save_payment_method}
                                            />
                                        </Suspense>
                                    );
                                } else {
                                    return (
                                        <ExpandButton
                                            key={`button_${x.id}`}
                                            onClick={() => {
                                                if (!x.is_disabled) {
                                                    setExpandedPaymentMethodId(
                                                        x.id
                                                    );
                                                }
                                            }}
                                            paymentMethods={x.payment_methods}
                                            isDisabled={x.is_disabled}
                                            minAmountForCreditRequired={currencyFormat(x.min_amount_for_credit_required, x.currency, lang)}
                                            refundFee={currencyFormat(x.fee, x.currency, lang)}
                                            isRefundContext={x.operation === 'credit'}
                                        />
                                    );
                                }
                            case 'paymentsos':
                                if (x.id === expandedPaymentMethodId) {
                                    return (
                                        <Suspense key={`expanded_${x.id}`} fallback={<>{loader}</>}>
                                            <PaymentsOS
                                                config={x.config}
                                                token={token}
                                                lyriaUrl={lyriaUrl}
                                                savePaymentMethod={x.save_payment_method}
                                                savedPaymentMethods={x.saved_payment_methods}
                                                onPaymentStarted={
                                                    paymentStartedCallback(x)
                                                }
                                                onPaymentResult={
                                                    paymentResultCallback
                                                }
                                                loader={loader}
                                                hidePayButton={hidePayButton}
                                                ref={expandedPaymentMethodRef}
                                            />
                                        </Suspense>
                                    );
                                } else {
                                    return (
                                        <ExpandButton
                                            key={`button_${x.id}`}
                                            onClick={() => {
                                                if (!x.is_disabled) {
                                                    setExpandedPaymentMethodId(
                                                        x.id
                                                    );
                                                }
                                            }}
                                            paymentMethods={x.payment_methods}
                                            isDisabled={x.is_disabled}
                                            minAmountForCreditRequired={currencyFormat(x.min_amount_for_credit_required, x.currency, lang)}
                                            refundFee={currencyFormat(x.fee, x.currency, lang)}
                                            isRefundContext={x.operation === 'credit'}
                                        />
                                    );
                                }
                            case 'iban':
                                if (x.id === expandedPaymentMethodId) {
                                    return (
                                        <Iban
                                            key={`expanded_${x.id}`}
                                            pspId={x.id}
                                            config={x.config}
                                            label={x.name}
                                            token={token}
                                            lyriaUrl={lyriaUrl}
                                            onPaymentResult={
                                                paymentResultCallback
                                            }
                                            hidePayButton={hidePayButton}
                                            ref={expandedPaymentMethodRef}
                                        />
                                    );
                                } else {
                                    return (
                                        <ExpandButton
                                            key={`button_${x.id}`}
                                            onClick={() => {
                                                if (!x.is_disabled) {
                                                    setExpandedPaymentMethodId(
                                                        x.id
                                                    );
                                                }
                                            }}
                                            paymentMethods={x.payment_methods}
                                            isDisabled={x.is_disabled}
                                            minAmountForCreditRequired={currencyFormat(x.min_amount_for_credit_required, x.currency, lang)}
                                            refundFee={currencyFormat(x.fee, x.currency, lang)}
                                            isRefundContext={x.operation === 'credit'}
                                        />
                                    );
                                }
                            case 'clabe':
                                if (x.id === expandedPaymentMethodId) {
                                    return (
                                        <Clabe
                                            key={`expanded_${x.id}`}
                                            pspId={x.id}
                                            label={x.name}
                                            token={token}
                                            lyriaUrl={lyriaUrl}
                                            onPaymentResult={
                                                paymentResultCallback
                                            }
                                            hidePayButton={hidePayButton}
                                            ref={expandedPaymentMethodRef}
                                        />
                                    );
                                } else {
                                    return (
                                        <ExpandButton
                                            key={`button_${x.id}`}
                                            onClick={() => {
                                                if (!x.is_disabled) {
                                                    setExpandedPaymentMethodId(
                                                        x.id
                                                    );
                                                }
                                            }}
                                            paymentMethods={x.payment_methods}
                                            isDisabled={x.is_disabled}
                                            minAmountForCreditRequired={currencyFormat(x.min_amount_for_credit_required, x.currency, lang)}
                                            refundFee={currencyFormat(x.fee, x.currency, lang)}
                                            isRefundContext={x.operation === 'credit'}
                                        />
                                    );
                                }
                            case 'international_bank':
                                if (x.id === expandedPaymentMethodId) {
                                    return (
                                        <InternationalBank
                                            key={`expanded_${x.id}`}
                                            pspId={x.id}
                                            label={x.name}
                                            token={token}
                                            lyriaUrl={lyriaUrl}
                                            onPaymentResult={
                                                paymentResultCallback
                                            }
                                            hidePayButton={hidePayButton}
                                            ref={expandedPaymentMethodRef}
                                        />
                                    );
                                } else {
                                    return (
                                        <ExpandButton
                                            key={`button_${x.id}`}
                                            onClick={() => {
                                                if (!x.is_disabled) {
                                                    setExpandedPaymentMethodId(
                                                        x.id
                                                    );
                                                }
                                            }}
                                            paymentMethods={x.payment_methods}
                                            isDisabled={x.is_disabled}
                                            minAmountForCreditRequired={currencyFormat(x.min_amount_for_credit_required, x.currency, lang)}
                                            refundFee={currencyFormat(x.fee, x.currency, lang)}
                                            isRefundContext={x.operation === 'credit'}
                                        />
                                    );
                                }
                            case 'ingenico':
                                if (x.id === expandedPaymentMethodId) {
                                    return (
                                        <Ingenico
                                            key={`expanded_${x.id}`}
                                            pspId={x.id}
                                            config={x.config}
                                            token={token}
                                            lyriaUrl={lyriaUrl}
                                            paymentMethods={x.payment_methods}
                                            label={x.name}
                                            ref={expandedPaymentMethodRef}
                                        />
                                    );
                                } else {
                                    return (
                                        <ExpandButton
                                            key={`button_${x.id}`}
                                            onClick={() => {
                                                if (!x.is_disabled) {
                                                    setExpandedPaymentMethodId(
                                                        x.id
                                                    );
                                                }
                                            }}
                                            paymentMethods={x.payment_methods}
                                            isDisabled={x.is_disabled}
                                            minAmountForCreditRequired={currencyFormat(x.min_amount_for_credit_required, x.currency, lang)}
                                            refundFee={currencyFormat(x.fee, x.currency, lang)}
                                            isRefundContext={x.operation === 'credit'}
                                        />
                                    );
                                }
                            default:
                                return null;
                        }
                    })
                    )}
                    {!displaySpinner && !paymentMethodsToDisplay.length && (
                        <div className={`wz-lyriapay-no-payment-method-available`}>
                            <Trans i18nKey="no_payment_method_available" />
                        </div>
                    )}
                </div>
            </I18nextProvider>
        );
    }
);

LyriaPay.propTypes = {
    token: PropTypes.string.isRequired,
    lyriaUrl: PropTypes.string.isRequired,
    onPaymentResult: PropTypes.func.isRequired,
    expiryAsText: PropTypes.bool,
    sandbox: PropTypes.bool,
    hidePayButton: PropTypes.bool,
    lang: PropTypes.string.isRequired,
    loader: PropTypes.node
};

LyriaPay.displayName = 'LyriaPay';

export default LyriaPay;
